import { useEffect, useRef, useState } from "react";
import { Sparkles, ChevronDown, Copy, Check } from "lucide-react";

type LlmTarget = {
  name: string;
  build: (prompt: string) => string;
};

const CANONICAL_HOST = "https://truwe.in";

const TARGETS: LlmTarget[] = [
  {
    // ChatGPT search mode — hints=search forces the browsing tool so it actually
    // fetches the URL instead of answering from memory.
    name: "ChatGPT",
    build: (p) => `https://chatgpt.com/?hints=search&q=${encodeURIComponent(p)}`,
  },
  {
    name: "Claude",
    build: (p) => `https://claude.ai/new?q=${encodeURIComponent(p)}`,
  },
  {
    // /search/new opens a fresh Perplexity thread with web search enabled;
    // the older /search?q= silently skipped the browse step for some accounts.
    name: "Perplexity",
    build: (p) => `https://www.perplexity.ai/search/new?q=${encodeURIComponent(p)}`,
  },
  {
    name: "Google AI Mode",
    build: (p) => `https://www.google.com/search?udm=50&q=${encodeURIComponent(p)}`,
  },
];

// Normalise any URL to the public canonical host so LLM fetchers hit the
// production page (never the preview/lovable.app host, which is not crawlable).
function toCanonical(u: string): string {
  if (!u) return CANONICAL_HOST + "/";
  try {
    const parsed = new URL(u, CANONICAL_HOST);
    return CANONICAL_HOST + parsed.pathname + parsed.search;
  } catch {
    return CANONICAL_HOST + "/";
  }
}

export function ReadWithLlm({
  title,
  url,
  kind = "page",
  className = "",
}: {
  title: string;
  url?: string;
  kind?: "product" | "article" | "page";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState(toCanonical(url ?? ""));
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (url) {
      setResolvedUrl(toCanonical(url));
    } else if (typeof window !== "undefined") {
      setResolvedUrl(toCanonical(window.location.pathname + window.location.search));
    }
  }, [url]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Explicit "browse this URL" verb pushes the assistant to invoke its web tool
  // instead of answering from memory. Title + URL together make it unambiguous.
  const prompt =
    kind === "product"
      ? `Please browse this TruWe product page and give me an unbiased summary, key ingredients, doses, clinical evidence, who it's for, and any concerns. Quote and cite the page.\n\nTitle: ${title}\nURL: ${resolvedUrl}`
      : kind === "article"
        ? `Please browse this TruWe article and summarize the evidence, dosing and takeaways in plain language, with citations.\n\nTitle: ${title}\nURL: ${resolvedUrl}`
        : `Please browse this page and summarize it in plain language, then answer any follow-up I ask.\n\nTitle: ${title}\nURL: ${resolvedUrl}`;

  return (
    <div ref={rootRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Read ${title} with an AI assistant`}
        className="inline-flex items-center gap-1.5 rounded-full border border-brand/30 bg-brand/5 px-3 py-1.5 text-xs font-medium text-brand-deep transition hover:border-brand/60 hover:bg-brand/10"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Read with AI
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-50 mt-2 w-56 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border/70 bg-background shadow-xl sm:left-auto sm:right-0"
        >
          <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            Open in
          </div>
          {TARGETS.map((t) => (
            <a
              key={t.name}
              role="menuitem"
              href={t.build(prompt)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between px-3 py-2.5 text-sm text-brand-deep transition hover:bg-brand/5"
            >
              <span>{t.name}</span>
              <span aria-hidden className="text-muted-foreground">↗</span>
            </a>
          ))}
          <div className="border-t border-border/70" />
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(prompt);
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
              } catch {
                // no-op — clipboard blocked
              }
            }}
            className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm text-brand-deep transition hover:bg-brand/5"
          >
            <span className="flex flex-col">
              <span>Copy prompt</span>
              <span className="text-[11px] font-normal text-muted-foreground">
                Paste into any other AI
              </span>
            </span>
            {copied ? (
              <Check className="h-3.5 w-3.5 text-brand" aria-hidden />
            ) : (
              <Copy className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            )}
          </button>
        </div>
      )}
    </div>
  );
}
